import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../api';
import { useToast } from '../components/Toast';
import { useAuthMe } from '../queries';
import { TIER_LIMITS, TIER_META } from '../subscriptions';

/* ─── Design tokens (cobalt) ─────────────────────────────── */
const INK  = '#0A1F4A';
const INK2 = '#3D4F78';
const INK3 = '#7883A2';
const BG   = '#ECEEF3';
const SURF = '#FFFFFF';
const RULE = '#D6DAE4';
const ACC  = '#FF7A3D';

/* ─── Paid tier display config ───────────────────────────── */
const PLANS = [
  {
    key: 'tier1',
    highlight: false,
    description: 'For solo businesses targeting one country.',
    features: [
      { text: 'Unlimited matches', included: true },
      { text: 'AI relevance scoring (0–100)', included: true },
      { text: '1 product profile', included: true },
      { text: 'Email alerts for new matches', included: true },
      { text: '1 country', included: true },
      { text: 'Daily match refresh', included: true },
      { text: 'API access (CRM integration)', included: false },
      { text: 'Search tool — browse all tenders', included: false },
      { text: 'CSV export', included: false },
    ],
  },
  {
    key: 'tier2',
    highlight: true,
    description: 'For businesses expanding across the EU.',
    features: [
      { text: 'Unlimited matches', included: true },
      { text: 'AI relevance scoring (0–100)', included: true },
      { text: `${TIER_LIMITS.tier2.max_profiles} product profiles`, included: true },
      { text: 'Email alerts for new matches', included: true },
      { text: 'All TED countries', included: true },
      { text: 'Daily match refresh', included: true },
      { text: 'API access (CRM integration)', included: true },
      { text: 'Search tool — browse all tenders', included: true },
      { text: 'CSV export', included: false },
    ],
  },
  {
    key: 'tier3',
    highlight: false,
    description: 'For agencies and larger procurement teams.',
    features: [
      { text: 'Unlimited matches', included: true },
      { text: 'AI relevance scoring (0–100)', included: true },
      { text: `${TIER_LIMITS.tier3.max_profiles} product profiles`, included: true },
      { text: 'Email alerts for new matches', included: true },
      { text: 'All TED countries', included: true },
      { text: 'Daily match refresh', included: true },
      { text: 'API access (CRM integration)', included: true },
      { text: 'Search tool — browse all tenders', included: true },
      { text: 'CSV export', included: true },
    ],
  },
];

const PAID_KEYS = ['tier1', 'tier2', 'tier3'];

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return null;
  }
}

export default function Billing() {
  const showToast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [busy, setBusy] = useState(null); // plan key | 'portal' while redirecting

  // Current subscription state (cached, shared, refreshes in the background).
  const meQuery = useAuthMe();
  const me = meQuery.data ?? null;
  const loading = meQuery.isLoading;

  // Gentle notice when the user returns from a cancelled checkout.
  useEffect(() => {
    if (searchParams.get('payment') === 'cancelled') {
      showToast('Checkout cancelled — no charge was made.', 'info');
      searchParams.delete('payment');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line

  async function startCheckout(planKey) {
    setBusy(planKey);
    try {
      const res = await apiFetch('/payments/create-checkout', {
        method: 'POST',
        body: JSON.stringify({ plan: planKey }),
      });
      if (res && res.checkout_url) {
        window.location.href = res.checkout_url; // leave the app for Stripe
      } else {
        throw new Error('Could not start checkout');
      }
    } catch (e) {
      showToast(e.message || 'Could not start checkout', 'error');
      setBusy(null);
    }
  }

  // plan = a target tier key → deep-link to Stripe's "confirm plan switch" flow.
  // Omitted → open the portal home (manage card / invoices / cancel).
  async function openPortal(plan) {
    setBusy(plan || 'portal');
    try {
      const res = await apiFetch('/payments/create-portal-session', {
        method: 'POST',
        body: JSON.stringify(plan ? { plan } : {}),
      });
      if (res && res.portal_url) {
        window.location.href = res.portal_url;
      } else {
        throw new Error('Could not open the billing portal');
      }
    } catch (e) {
      showToast(e.message || 'Could not open the billing portal', 'error');
      setBusy(null);
    }
  }

  if (loading) {
    return <div style={{ ...s.page, color: INK3 }}>Loading…</div>;
  }

  const status   = me?.subscription_status;
  const isPaid    = !!me?.is_paid;            // active paid subscription
  const isTrial   = status === 'trialing';
  const currentTier = me?.tier;               // 'trial' while trialing, else real tier
  const trialDays = me?.trial_days_remaining;
  const cancelAtPeriodEnd = !!me?.cancel_at_period_end;
  const periodEndLabel = fmtDate(me?.subscription_ends_at);

  return (
    <div style={s.page}>
      <h1 style={s.heading}>Billing &amp; plan</h1>

      {/* ── Status banner ───────────────────────────────────── */}
      <div style={s.statusCard}>
        {isPaid ? (
          <>
            <div>
              <div style={s.statusLabel}>Current plan</div>
              <div style={s.statusPlan}>
                {TIER_META[currentTier]?.label || currentTier}
                <span style={s.statusPrice}>
                  €{TIER_META[currentTier]?.price_eur}/mo
                </span>
              </div>
              {cancelAtPeriodEnd ? (
                <div style={s.cancelNotice}>
                  Your plan is set to cancel{periodEndLabel ? ` on ${periodEndLabel}` : ''}.
                  You keep {TIER_META[currentTier]?.label} access until then, after which you move to Free.
                </div>
              ) : periodEndLabel ? (
                <div style={s.statusSub}>Renews on {periodEndLabel}.</div>
              ) : null}
            </div>
            <button
              style={{ ...s.manageBtn, opacity: busy ? 0.6 : 1 }}
              disabled={!!busy}
              onClick={() => openPortal()}
            >
              {busy === 'portal' ? 'Opening…' : 'Manage subscription'}
            </button>
          </>
        ) : (
          <div>
            <div style={s.statusLabel}>Current plan</div>
            <div style={s.statusPlan}>
              {isTrial ? 'Free trial' : 'Free (trial ended)'}
            </div>
            <div style={s.statusSub}>
              {isTrial
                ? `${trialDays != null ? trialDays : 14} day${trialDays === 1 ? '' : 's'} left. Pick a plan below to keep your matches and unlock email alerts, more countries, and daily refresh.`
                : 'Your matches and profile are saved. Choose a plan below to reactivate them.'}
            </div>
          </div>
        )}
      </div>

      {/* ── Plan grid ───────────────────────────────────────── */}
      <div style={s.grid}>
        {PLANS.map((plan) => {
          const meta = TIER_META[plan.key];
          const isCurrent = isPaid && currentTier === plan.key;
          const hl = plan.highlight;

          let cta;
          if (isCurrent) {
            cta = <div style={s.currentTag}>✓ Your current plan</div>;
          } else if (isPaid) {
            // Deep-link to Stripe's plan-switch confirmation flow for THIS plan,
            // so we never create a second subscription and the user lands on a
            // "confirm switch" page (with proration) rather than the cancel page.
            const isUpgrade = PAID_KEYS.indexOf(plan.key) > PAID_KEYS.indexOf(currentTier);
            cta = (
              <button
                style={{ ...(hl ? s.ctaPrimary : s.ctaSecondary), opacity: busy ? 0.6 : 1 }}
                disabled={!!busy}
                onClick={() => openPortal(plan.key)}
              >
                {busy === plan.key ? 'Redirecting…' : isUpgrade ? `Upgrade to ${meta.label}` : `Switch to ${meta.label}`}
              </button>
            );
          } else {
            cta = (
              <button
                style={{ ...(hl ? s.ctaPrimary : s.ctaSecondary), opacity: busy ? 0.6 : 1 }}
                disabled={!!busy}
                onClick={() => startCheckout(plan.key)}
              >
                {busy === plan.key ? 'Redirecting…' : `Choose ${meta.label}`}
              </button>
            );
          }

          return (
            <div key={plan.key} style={hl ? s.cardHighlight : s.card}>
              {hl && <div style={s.popularTag}>MOST POPULAR</div>}
              <div style={{ ...s.planName, color: hl ? ACC : INK3 }}>{meta.label}</div>
              <div style={s.priceRow}>
                <span style={{ ...s.price, color: hl ? BG : INK }}>€{meta.price_eur}</span>
                <span style={{ ...s.priceUnit, color: hl ? 'rgba(236,238,243,0.55)' : INK3 }}>/ month</span>
              </div>
              <p style={{ ...s.desc, color: hl ? 'rgba(236,238,243,0.7)' : INK2 }}>{plan.description}</p>
              {cta}
              <div style={{ ...s.featureDivider, borderTop: `1px solid ${hl ? 'rgba(255,255,255,0.12)' : RULE}` }}>
                <ul style={s.featureList}>
                  {plan.features.map((f) => (
                    <li
                      key={f.text}
                      style={{
                        ...s.feature,
                        color: f.included
                          ? (hl ? 'rgba(236,238,243,0.85)' : INK2)
                          : (hl ? 'rgba(236,238,243,0.3)' : INK3),
                      }}
                    >
                      <span
                        style={{
                          ...s.check,
                          background: f.included ? ACC : 'transparent',
                          boxShadow: f.included ? 'none' : `inset 0 0 0 1px ${RULE}`,
                        }}
                      >
                        {f.included ? '✓' : ''}
                      </span>
                      {f.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      <div style={s.footnote}>
        Secure payment by Stripe · Cancel any time · All prices excl. VAT
      </div>
    </div>
  );
}

const s = {
  page: {
    maxWidth: 1000,
    margin: '0 auto',
    padding: '40px 24px 80px',
    background: BG,
    minHeight: '100vh',
  },
  heading: {
    fontSize: 30, fontWeight: 700, color: INK,
    letterSpacing: '-0.02em', marginBottom: 24,
  },

  statusCard: {
    background: SURF,
    border: `1px solid ${RULE}`,
    borderLeft: `4px solid ${ACC}`,
    borderRadius: 14,
    padding: '20px 24px',
    marginBottom: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
    flexWrap: 'wrap',
  },
  statusLabel: {
    fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
    color: INK3, fontFamily: '"JetBrains Mono", monospace', marginBottom: 6,
  },
  statusPlan: { fontSize: 20, fontWeight: 700, color: INK },
  statusPrice: { fontSize: 13, fontWeight: 500, color: INK3, marginLeft: 10 },
  statusSub: { fontSize: 13, color: INK2, marginTop: 6, maxWidth: 620, lineHeight: 1.5 },
  cancelNotice: { fontSize: 13, color: '#B45309', fontWeight: 500, marginTop: 8, maxWidth: 620, lineHeight: 1.5 },
  manageBtn: {
    background: INK, color: BG, border: 'none',
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
    padding: '11px 22px', borderRadius: 10, whiteSpace: 'nowrap',
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 20,
    alignItems: 'start',
  },
  card: {
    background: SURF, border: `1px solid ${RULE}`, borderRadius: 16,
    padding: '32px 22px 28px', display: 'flex', flexDirection: 'column', gap: 16,
    position: 'relative',
  },
  cardHighlight: {
    background: INK, border: `1px solid ${INK}`, borderRadius: 16,
    padding: '40px 22px 36px', display: 'flex', flexDirection: 'column', gap: 16,
    position: 'relative',
    boxShadow: '0 20px 40px -8px rgba(10,31,74,0.45)',
  },
  popularTag: {
    position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
    background: ACC, color: '#fff',
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 11, fontWeight: 600, letterSpacing: '0.14em',
    padding: '6px 14px', borderRadius: 999,
    border: `1px solid ${INK}`, whiteSpace: 'nowrap',
  },
  planName: {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
  },
  priceRow: { display: 'flex', alignItems: 'baseline', gap: 2 },
  price: { fontSize: 44, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1 },
  priceUnit: { fontSize: 12, fontFamily: '"JetBrains Mono", monospace', marginLeft: 6 },
  desc: { fontSize: 13, minHeight: 44, margin: 0 },

  ctaPrimary: {
    display: 'block', width: '100%', textAlign: 'center',
    background: ACC, color: '#fff', border: 'none',
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
    padding: '11px 16px', borderRadius: 10,
    boxShadow: '0 4px 0 0 rgba(0,0,0,0.25)',
  },
  ctaSecondary: {
    display: 'block', width: '100%', textAlign: 'center',
    background: 'transparent', color: INK, border: `1px solid ${INK}`,
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
    padding: '11px 16px', borderRadius: 10,
  },
  currentTag: {
    textAlign: 'center', fontSize: 13, fontWeight: 600,
    color: ACC, padding: '11px 16px',
    border: `1px dashed ${ACC}`, borderRadius: 10,
  },

  featureDivider: { paddingTop: 16 },
  featureList: {
    listStyle: 'none', padding: 0, margin: 0,
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  feature: { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 },
  check: {
    width: 14, height: 14, flexShrink: 0, marginTop: 3,
    borderRadius: 4, color: '#fff',
    fontSize: 9, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },

  footnote: {
    marginTop: 28, textAlign: 'center',
    fontSize: 12, color: INK3, fontFamily: '"JetBrains Mono", monospace',
  },
};
